import { ref } from '@vue/composition-api'
import {
  format, startOfMonth, subMonths,
} from 'date-fns'
import DataIntegrityDiagnosticApi from '@/api/v2/eod-closing/DataIntegrityDiagnostic'

// サイドバーバッジ用に、未保存有無をアプリ全体で共有する singleton state。
// 全画面共通で表示されるため、複数回マウントされても 1 回だけ取得すれば足りる。
const hasUnsaved = ref(false)
const badgeLoaded = ref(false)
// 集計コンテナが用意されているか。null=未取得 / true=作成済み / false=未作成。
// 「eod_report 権限はあるがコンテナ未作成」をサイドバーで警告するために使う。
const containerConfigured = ref(null)
let inflightBadgeRequest = null

/**
 * 診断対象期間（前月初日 〜 今日）を返す。
 * @param {string} today - 店舗の「今日」(YYYY-MM-DD)。未指定ならローカル日付。
 */
export const diagnosticDateRange = today => {
  const base = new Date()
  const from = format(startOfMonth(subMonths(base, 1)), 'yyyy-MM-dd')
  const to = today || format(base, 'yyyy-MM-dd')

  return { from, to }
}

/**
 * データ整合性診断のフロント用ロジックを提供する composable。
 *
 * - `fetchDiagnostics`: 日報βの未保存UI向け。summary ＋ 営業日ごとの rows を取得する。
 * - `fetchBadge`: サイドバーバッジ向け。未保存有無の軽量レスポンスを取得し singleton に反映する。
 */
export default function useDataIntegrityDiagnostics() {
  const loading = ref(false)
  const error = ref(null)
  const summary = ref(null)
  const rows = ref([])

  // 日報βの未保存UI用: 期間内の診断結果（summary ＋ 営業日ごとの rows）を取得する。
  const fetchDiagnostics = async ({ from, to }) => {
    loading.value = true
    error.value = null
    try {
      const res = await DataIntegrityDiagnosticApi.getDiagnostics(from, to)
      // TODO(backend): レスポンスのラッパー構造（status/data ラップの有無）は
      // バックエンド実装に合わせて要確認。現状は素のオブジェクトを想定し、
      // status ラッパーがあればその下を見るよう両対応にしておく。
      const payload = res.data?.data ?? res.data ?? {}
      summary.value = payload.summary ?? null
      rows.value = payload.rows ?? []
      // バッジ用 singleton も併せて更新する（同じ期間を診ているため流用できる）。
      hasUnsaved.value = (summary.value?.total_findings ?? 0) > 0
      containerConfigured.value = payload.aggregation_container_configured ?? null
      badgeLoaded.value = true
    } catch (e) {
      error.value = '診断結果を取得できませんでした'
      summary.value = null
      rows.value = []
      containerConfigured.value = null
    } finally {
      loading.value = false
    }
  }

  // サイドバーバッジ用: 未保存有無の軽量レスポンスを取得する。
  // 全画面共通で表示されるため、取得済みなら再取得しない（force で強制再取得）。
  //
  // 注意: summary_only レスポンスはサーバ側で club×期間 10分キャッシュされる。
  // 日報βのフル取得(fetchDiagnostics)は非キャッシュで、その際サーバが同一期間の
  // バッジキャッシュも最新化する(write-through)ため、点灯が古いまま残るのは
  // 「誰も日報βを開いていない状態での新規発生」が最大10分遅れるケースのみ。
  const fetchBadge = async ({ from, to, force = false } = {}) => {
    if (badgeLoaded.value && !force) return hasUnsaved.value
    if (inflightBadgeRequest) return inflightBadgeRequest

    const range = from && to ? { from, to } : diagnosticDateRange()
    inflightBadgeRequest = (async () => {
      try {
        const res = await DataIntegrityDiagnosticApi.getDiagnosticsSummary(range.from, range.to)
        const payload = res.data?.data ?? res.data ?? {}
        hasUnsaved.value = !!payload.has_unsaved
        // 旧バックエンド（フィールド無し）では null のままにし、警告を出さない。
        containerConfigured.value = payload.aggregation_container_configured ?? null
        badgeLoaded.value = true
      } catch (e) {
        // バッジは付加機能のため、取得失敗時は点灯させず黙って諦める。
        hasUnsaved.value = false
        containerConfigured.value = null
      } finally {
        inflightBadgeRequest = null
      }

      return hasUnsaved.value
    })()

    return inflightBadgeRequest
  }

  return {
    loading,
    error,
    summary,
    rows,
    hasUnsaved,
    containerConfigured,
    badgeLoaded,
    fetchDiagnostics,
    fetchBadge,
    diagnosticDateRange,
  }
}
